import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api, setAuthToken } from "@/lib/api";
import Home from "@/pages/Home";
import Browse from "@/pages/Browse";
import Admin from "@/pages/Admin";
import MyUploads from "@/pages/MyUploads";
import Profile from "@/pages/Profile";
import Inbox from "@/pages/Inbox";
import Messages from "@/pages/Messages";
import MemberProfile from "@/pages/MemberProfile";
import ExploreMap from "@/pages/ExploreMap";
import LocationPage from "@/pages/LocationPage";
import Battles from "@/pages/Battles";
import Blind from "@/pages/Blind";
import GalleryPage from "@/pages/GalleryPage";
import Requests from "@/pages/Requests";
import Leaderboards from "@/pages/Leaderboards";
import Swipe from "@/pages/Swipe";
import RateLater from "@/pages/RateLater";
import Collection from "@/pages/Collection";
import Galleries from "@/pages/Galleries";
import Login from "@/pages/Login";
import OwnerSettings from "@/pages/OwnerSettings";
import OwnerMobile from "@/pages/OwnerMobile";
import VerifyEmail from "@/pages/VerifyEmail";
import ResetPassword from "@/pages/ResetPassword";
import { PersonaSetup } from "@/components/PersonaSetup";
import { JoinGate } from "@/components/JoinGate";
import { AuthDialog } from "@/components/AuthDialog";
import { FloatingAI } from "@/components/FloatingAI";
import { MobileSwipeFab } from "@/components/MobileSwipeFab";
import { Loader2 } from "lucide-react";

const AuthCallback = () => {
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? match[1] : null;
    const run = async () => {
      // 1) Exchange the single-use session_id for an httpOnly session cookie.
      let exchanged = false;
      for (let i = 0; i < 3 && !exchanged; i++) {
        try {
          const res = await api.post("/auth/session", {}, { headers: { "X-Session-ID": sessionId } });
          // Bearer fallback: persist the token so auth works even if the cookie is blocked/delayed
          // on the custom domain (Safari ITP / partitioned SameSite=None cookies).
          if (res?.data?.session_token) setAuthToken(res.data.session_token);
          exchanged = true;
        } catch (e) {
          console.error("Session exchange error", e);
          await new Promise((r) => setTimeout(r, 400));
        }
      }
      // 2) Clear the hash and HARD-reload to "/". A full reload guarantees the browser sends the
      //    freshly-set httpOnly cookie (SPA polling raced the cookie on iOS Safari and showed a
      //    false "didn't complete" error). The app's own /auth/me on load then confirms sign-in.
      window.history.replaceState(null, "", "/");
      if (!exchanged) {
        toast.error("Sign-in didn't complete — please tap Sign in again");
      }
      window.location.replace("/");
    };
    if (sessionId) run();
    else window.location.replace("/");
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
      <p className="text-muted-foreground text-sm tracking-widest uppercase">Entering the reel…</p>
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/browse" element={<Browse />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/my-uploads" element={<MyUploads />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/inbox" element={<Inbox />} />
      <Route path="/owner" element={<OwnerMobile />} />
      <Route path="/owner/settings" element={<OwnerSettings />} />
      <Route path="/messages" element={<Messages />} />
      <Route path="/u/:userId" element={<MemberProfile />} />
      <Route path="/map" element={<ExploreMap />} />
      <Route path="/place/:slug" element={<LocationPage />} />
      <Route path="/battles" element={<Battles />} />
      <Route path="/blind" element={<Blind />} />
      <Route path="/gallery/:gid" element={<GalleryPage />} />
      <Route path="/requests" element={<Requests />} />
      <Route path="/leaderboards" element={<Leaderboards />} />
      <Route path="/swipe" element={<Swipe />} />
      <Route path="/rate-later" element={<RateLater />} />
      <Route path="/collection/:cid" element={<Collection />} />
      <Route path="/galleries" element={<Galleries />} />
      <Route path="/login" element={<Login />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <div className="grain-overlay" />
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <PersonaSetup />
          <JoinGate />
          <AuthDialog />
          <div className="fixed bottom-4 right-4 z-[900] flex items-center gap-2 rounded-full bg-black/80 backdrop-blur-md border border-primary/25 p-1.5 shadow-2xl shadow-black/50">
            <MobileSwipeFab />
            <FloatingAI />
          </div>
          <Toaster position="top-center" theme="dark" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
